import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Stethoscope,
  LogOut,
  Settings as SettingsIcon,
  Users,
  BarChart3,
  Shield,
  Loader2,
  Plus,
  Check,
  Trash2,
  LayoutDashboard,
  History,
  Cpu,
  Star,
  ChevronRight,
  ChevronLeft,
  ShieldCheck,
  Fingerprint,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SEO } from "@/components/SEO";
import { format } from "date-fns";

const MAX_RECORDING_OPTIONS = [
  { key: "30m", label: "30 minutes", ms: 30 * 60 * 1000 },
  { key: "1h", label: "1 hour", ms: 60 * 60 * 1000 },
  { key: "2h", label: "2 hours", ms: 2 * 60 * 60 * 1000 },
  { key: "3h", label: "3 hours", ms: 3 * 60 * 60 * 1000 },
];

function msToOptionKey(ms: number): string {
  const match = MAX_RECORDING_OPTIONS.find((o) => o.ms === ms);
  return match?.key ?? "1h";
}

function AdminHeader({ user, logout }: { user: any; logout: () => void }) {
  const isAdmin = useIsAdmin();

  return (
    <header className="h-12 border-b border-border bg-card flex items-center justify-between px-4 shrink-0">
      <div className="flex items-center gap-2">
        <Link
          href="/dashboard"
          className="flex items-center gap-1.5"
          aria-label="LRScribe home"
        >
          <div className="p-1 bg-primary/10 rounded-sm">
            <Stethoscope className="w-4 h-4 text-primary" />
          </div>
          <span className="font-heading font-bold text-sm text-foreground hidden sm:inline">
            LRScribe
          </span>
        </Link>
        <span className="text-muted-foreground text-xs">/ Admin</span>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <ThemeToggle className="h-6 w-6 [&_svg]:w-3.5 [&_svg]:h-3.5" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Open user menu"
              className="flex items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="button-user-menu"
            >
              <Avatar className="h-6 w-6 pointer-events-none">
                <AvatarImage src={user?.profileImageUrl || undefined} />
                <AvatarFallback className="bg-primary/10 text-primary text-[10px]">
                  {user?.firstName?.charAt(0) || user?.email?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="text-sm font-medium text-foreground truncate" data-testid="text-user-name">
                {user?.firstName
                  ? `${user.firstName} ${user.lastName || ""}`.trim()
                  : user?.email}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link href="/dashboard" data-testid="link-dashboard">
                <LayoutDashboard className="w-4 h-4 mr-2" />
                Dashboard
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href="/settings" data-testid="link-settings">
                <SettingsIcon className="w-4 h-4 mr-2" />
                Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href="/security" data-testid="link-security">
                <Shield className="w-4 h-4 mr-2" />
                Security
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href="/change-log" data-testid="link-change-log">
                <History className="w-4 h-4 mr-2" />
                Change log
              </Link>
            </DropdownMenuItem>
            {isAdmin && (
              <DropdownMenuItem asChild>
                <Link href="/admin/settings" data-testid="link-admin">
                  <Shield className="w-4 h-4 mr-2" />
                  Admin
                </Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={logout}
              className="text-destructive focus:text-destructive"
              data-testid="button-logout"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

const ADMIN_NAV_ITEMS = [
  {
    href: "/admin/settings",
    label: "Settings",
    icon: SettingsIcon,
    testId: "link-admin-settings",
  },
  {
    href: "/admin/site-admins",
    label: "Site Admins",
    icon: Users,
    testId: "link-admin-site-admins",
  },
  {
    href: "/admin/usage",
    label: "Usage",
    icon: BarChart3,
    testId: "link-admin-usage",
  },
  {
    href: "/admin/models",
    label: "Models",
    icon: Cpu,
    testId: "link-admin-models",
  },
  {
    href: "/admin/identities",
    label: "Identities",
    icon: Fingerprint,
    testId: "link-admin-identities",
  },
] as const;

function AdminSidebar() {
  const [location] = useLocation();

  return (
    <aside className="hidden md:flex w-52 border-r border-border bg-card/30 flex-col shrink-0">
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold text-foreground">Site Admin</span>
        </div>
      </div>
      <nav className="p-2 flex flex-col gap-1">
        {ADMIN_NAV_ITEMS.map(({ href, label, icon: Icon, testId }) => {
          const isActive =
            location === href ||
            (location === "/admin" && href === "/admin/settings");
          return (
            <Link
              key={href}
              href={href}
              data-testid={testId}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              }`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              {label}
            </Link>
          );
        })}
      </nav>
    </aside>
  );
}

function AdminMobileMenu() {
  return (
    <nav
      className="md:hidden flex-1 overflow-auto bg-background"
      data-testid="nav-admin-mobile"
    >
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <Shield className="w-4 h-4 text-primary" />
        <span className="text-sm font-semibold text-foreground">Site Admin</span>
      </div>
      <ul className="divide-y divide-border">
        {ADMIN_NAV_ITEMS.map(({ href, label, icon: Icon, testId }) => (
          <li key={href}>
            <Link
              href={href}
              data-testid={`${testId}-mobile`}
              className="flex items-center gap-3 px-4 py-4 text-sm font-medium text-foreground hover:bg-muted/50 active:bg-muted transition-colors"
            >
              <Icon className="w-5 h-5 text-muted-foreground shrink-0" />
              <span className="flex-1">{label}</span>
              <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function AdminMobileBackBar() {
  const [location] = useLocation();
  const current = ADMIN_NAV_ITEMS.find((i) => i.href === location);

  return (
    <div className="md:hidden flex items-center gap-2 px-2 h-11 border-b border-border bg-card shrink-0">
      <Link
        href="/admin"
        data-testid="link-admin-back"
        className="flex items-center gap-0.5 px-2 py-1 -ml-1 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
      >
        <ChevronLeft className="w-4 h-4 shrink-0" />
        Menu
      </Link>
      {current && (
        <span className="text-sm font-semibold text-foreground truncate">
          {current.label}
        </span>
      )}
    </div>
  );
}

function AdminSettingsPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [saving, setSaving] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);

  // Experimental users state
  const [newExpEmail, setNewExpEmail] = useState("");
  const [addingExp, setAddingExp] = useState(false);
  const [removingExpEmail, setRemovingExpEmail] = useState<string | null>(null);

  const { data: siteConfigData } = useQuery<{ maxRecordingMs: number }>({
    queryKey: ["/api/admin/config"],
  });

  const { data: experimentalUsers, isLoading: expLoading } = useQuery<
    { id: number; email: string; addedAt: string }[]
  >({
    queryKey: ["/api/admin/experimental-users"],
  });

  const currentMs = siteConfigData?.maxRecordingMs ?? 60 * 60 * 1000;
  const currentKey = msToOptionKey(currentMs);

  const handleSelect = async (key: string) => {
    if (key === currentKey || saving) return;
    setSaving(key);
    try {
      await apiRequest("PUT", "/api/admin/settings", { optionKey: key });
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/config"] });
      setSavedKey(key);
      setTimeout(() => setSavedKey(null), 2000);
      toast({ title: "Saved", description: "Max recording time updated." });
    } catch (err) {
      toast({
        title: "Couldn't save",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(null);
    }
  };

  const handleAddExp = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newExpEmail.trim();
    if (!trimmed) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast({ title: "Invalid email", description: "Enter a valid email address.", variant: "destructive" });
      return;
    }
    setAddingExp(true);
    try {
      await apiRequest("POST", "/api/admin/experimental-users", { email: trimmed });
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/experimental-users"] });
      setNewExpEmail("");
      toast({ title: "Added", description: `${trimmed} now has experimental features enabled.` });
    } catch (err) {
      toast({
        title: "Couldn't add",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setAddingExp(false);
    }
  };

  const handleRemoveExp = async (targetEmail: string) => {
    try {
      await apiRequest("DELETE", `/api/admin/experimental-users/${encodeURIComponent(targetEmail)}`);
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/experimental-users"] });
      toast({ title: "Removed", description: `${targetEmail} no longer has experimental features enabled.` });
    } catch (err) {
      toast({
        title: "Couldn't remove",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setRemovingExpEmail(null);
    }
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-xl mx-auto px-6 py-8 space-y-8">
        <div>
          <h1 className="text-xl font-bold text-foreground">Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Site-wide configuration that applies to all users.
          </p>
        </div>

        <section className="space-y-4">
          <div>
            <h2 className="text-base font-semibold text-foreground">
              Maximum recording time
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Recordings automatically stop when they reach this limit. Applies to all users.
            </p>
          </div>
          <div className="border border-border rounded-lg p-4 bg-card space-y-2">
            {siteConfigData === undefined ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading…
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {MAX_RECORDING_OPTIONS.map((opt) => {
                  const isSelected = opt.key === currentKey;
                  const isSaving = saving === opt.key;
                  const isSaved = savedKey === opt.key;
                  return (
                    <button
                      key={opt.key}
                      onClick={() => handleSelect(opt.key)}
                      disabled={!!saving}
                      data-testid={`button-max-recording-${opt.key}`}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
                        isSelected
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background text-foreground border-border hover:border-primary/50 hover:bg-primary/5"
                      } disabled:opacity-60`}
                    >
                      {isSaving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                      {isSaved && !isSaving && <Check className="w-3.5 h-3.5" />}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <section className="space-y-4">
          <div>
            <h2 className="text-base font-semibold text-foreground">
              Experimental features
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Users on this list get access to experimental features that are not yet available to everyone.
            </p>
          </div>

          <form onSubmit={handleAddExp} className="flex gap-2">
            <Input
              type="email"
              placeholder="email@example.com"
              value={newExpEmail}
              onChange={(e) => setNewExpEmail(e.target.value)}
              className="flex-1"
              data-testid="input-new-experimental-email"
              disabled={addingExp}
            />
            <Button type="submit" disabled={addingExp || !newExpEmail.trim()} data-testid="button-add-experimental">
              {addingExp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add
            </Button>
          </form>

          <div className="border border-border rounded-lg overflow-hidden bg-card">
            {expLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : !experimentalUsers || experimentalUsers.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4 text-center">No users with experimental features.</p>
            ) : (
              <ul className="divide-y divide-border">
                {experimentalUsers.map((u) => (
                  <li
                    key={u.id}
                    className="flex items-center justify-between px-4 py-3"
                    data-testid={`row-experimental-${u.email}`}
                  >
                    <div>
                      <p className="text-sm font-medium text-foreground" data-testid={`text-experimental-email-${u.email}`}>
                        {u.email}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Added {format(new Date(u.addedAt), "MMM d, yyyy")}
                      </p>
                    </div>
                    <button
                      onClick={() => setRemovingExpEmail(u.email)}
                      title="Remove from experimental features"
                      className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                      data-testid={`button-remove-experimental-${u.email}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      <AlertDialog open={!!removingExpEmail} onOpenChange={(open) => { if (!open) setRemovingExpEmail(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from experimental features?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-foreground">{removingExpEmail}</span> will no longer have access to experimental features.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-remove-experimental">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => removingExpEmail && handleRemoveExp(removingExpEmail)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-remove-experimental"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AdminSiteAdminsPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [newEmail, setNewEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [removingEmail, setRemovingEmail] = useState<string | null>(null);

  const { data: admins, isLoading: adminsLoading } = useQuery<
    { id: number; email: string; addedAt: string }[]
  >({
    queryKey: ["/api/admin/admins"],
  });

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newEmail.trim();
    if (!trimmed) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast({ title: "Invalid email", description: "Enter a valid email address.", variant: "destructive" });
      return;
    }
    setAdding(true);
    try {
      await apiRequest("POST", "/api/admin/admins", { email: trimmed });
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/admins"] });
      setNewEmail("");
      toast({ title: "Admin added", description: `${trimmed} is now a site admin.` });
    } catch (err) {
      toast({
        title: "Couldn't add admin",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (targetEmail: string) => {
    try {
      await apiRequest("DELETE", `/api/admin/admins/${encodeURIComponent(targetEmail)}`);
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/admins"] });
      toast({ title: "Admin removed", description: `${targetEmail} is no longer a site admin.` });
    } catch (err) {
      toast({
        title: "Couldn't remove admin",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setRemovingEmail(null);
    }
  };

  const adminCount = admins?.length ?? 0;

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-xl mx-auto px-6 py-8 space-y-8">
        <div>
          <h1 className="text-xl font-bold text-foreground">Site Admins</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage who has access to this admin area.
          </p>
        </div>

        <section className="space-y-4">
          <form onSubmit={handleAdd} className="flex gap-2">
            <Input
              type="email"
              placeholder="email@example.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="flex-1"
              data-testid="input-new-admin-email"
              disabled={adding}
            />
            <Button type="submit" disabled={adding || !newEmail.trim()} data-testid="button-add-admin">
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add
            </Button>
          </form>

          <div className="border border-border rounded-lg overflow-hidden bg-card">
            {adminsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : !admins || admins.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4 text-center">No admins found.</p>
            ) : (
              <ul className="divide-y divide-border">
                {admins.map((admin) => (
                  <li
                    key={admin.id}
                    className="flex items-center justify-between px-4 py-3"
                    data-testid={`row-admin-${admin.email}`}
                  >
                    <div>
                      <p className="text-sm font-medium text-foreground" data-testid={`text-admin-email-${admin.email}`}>
                        {admin.email}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Added {format(new Date(admin.addedAt), "MMM d, yyyy")}
                      </p>
                    </div>
                    <button
                      onClick={() => setRemovingEmail(admin.email)}
                      disabled={adminCount <= 1}
                      title={adminCount <= 1 ? "Cannot remove the last admin" : "Remove admin"}
                      className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`button-remove-admin-${admin.email}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      <AlertDialog open={!!removingEmail} onOpenChange={(open) => { if (!open) setRemovingEmail(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove site admin?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-foreground">{removingEmail}</span> will no longer have access to the admin area.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-remove-admin">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => removingEmail && handleRemove(removingEmail)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-remove-admin"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface UsageRow {
  email: string;
  name: string | null;
  recordingCount: number;
  totalMinutes: number;
  lastActive: number;
}

interface UsageData {
  totalRecordings: number;
  totalMinutes: number;
  totalUsers: number;
  perUser: UsageRow[];
}

function AdminUsagePage() {
  const { data: usage, isLoading } = useQuery<UsageData>({
    queryKey: ["/api/admin/usage"],
  });

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">
        <div>
          <h1 className="text-xl font-bold text-foreground">Usage</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Platform-wide usage statistics.
          </p>
        </div>

        {isLoading || !usage ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <section className="grid grid-cols-3 gap-4">
              <div
                className="border border-border rounded-lg p-4 bg-card text-center"
                data-testid="stat-total-recordings"
              >
                <p className="text-2xl font-bold text-foreground">{usage.totalRecordings}</p>
                <p className="text-xs text-muted-foreground mt-1">Total recordings</p>
              </div>
              <div
                className="border border-border rounded-lg p-4 bg-card text-center"
                data-testid="stat-total-minutes"
              >
                <p className="text-2xl font-bold text-foreground">{usage.totalMinutes}</p>
                <p className="text-xs text-muted-foreground mt-1">Total minutes recorded</p>
              </div>
              <div
                className="border border-border rounded-lg p-4 bg-card text-center"
                data-testid="stat-total-users"
              >
                <p className="text-2xl font-bold text-foreground">{usage.totalUsers}</p>
                <p className="text-xs text-muted-foreground mt-1">Total users</p>
              </div>
            </section>

            <section className="space-y-3">
              <h2 className="text-base font-semibold text-foreground">Per-user breakdown</h2>
              <div className="border border-border rounded-lg overflow-hidden bg-card">
                {usage.perUser.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-4 text-center">No user activity yet.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">User</th>
                        <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Recordings</th>
                        <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Minutes</th>
                        <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Last active</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {usage.perUser.map((row) => (
                        <tr key={row.email} data-testid={`row-usage-${row.email}`}>
                          <td className="px-4 py-2.5">
                            <p className="font-medium text-foreground truncate max-w-[200px]" data-testid={`text-usage-email-${row.email}`}>
                              {row.email}
                            </p>
                            {row.name && (
                              <p className="text-xs text-muted-foreground truncate">{row.name}</p>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-right text-foreground" data-testid={`text-usage-count-${row.email}`}>
                            {row.recordingCount}
                          </td>
                          <td className="px-4 py-2.5 text-right text-foreground" data-testid={`text-usage-minutes-${row.email}`}>
                            {row.totalMinutes}
                          </td>
                          <td className="px-4 py-2.5 text-right text-muted-foreground text-xs" data-testid={`text-usage-last-active-${row.email}`}>
                            {row.lastActive
                              ? format(new Date(row.lastActive), "MMM d, yyyy")
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

type LlmConfig = {
  provider: "requesty" | "bedrock";
  defaultTier: "thinking" | "small";
  verifyTier: "thinking" | "small";
  genModelId: string;
  verifyModelId: string;
  models?: Record<"requesty" | "bedrock", Record<"thinking" | "small", string>>;
};

const TIER_LABELS: Record<string, string> = {
  thinking: "Thinking (more capable, slower)",
  small: "Small (faster, lighter)",
};

function AdminModelsPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resettingAll, setResettingAll] = useState(false);

  const [verifyIterationsSelection, setVerifyIterationsSelection] = useState<string>("");
  const [savingVerifyIterations, setSavingVerifyIterations] = useState(false);

  const [savingLlmConfig, setSavingLlmConfig] = useState(false);
  const [providerSelection, setProviderSelection] = useState<"requesty" | "bedrock">("requesty");
  const [defaultTierSelection, setDefaultTierSelection] = useState<"thinking" | "small">("thinking");
  const [verifyTierSelection, setVerifyTierSelection] = useState<"thinking" | "small">("small");

  const { data: llmConfig, isLoading: llmConfigLoading } = useQuery<LlmConfig>({
    queryKey: ["/api/admin/llm-config"],
  });

  const { data: verifyIterationsData, isLoading: verifyIterationsLoading } = useQuery<{
    maxIterations: number;
    source: "db" | "default";
    defaultValue: number;
    limit: number;
  }>({
    queryKey: ["/api/admin/verify-iterations"],
  });

  useEffect(() => {
    if (llmConfig) {
      setProviderSelection(llmConfig.provider);
      setDefaultTierSelection(llmConfig.defaultTier);
      setVerifyTierSelection(llmConfig.verifyTier);
    }
  }, [llmConfig?.provider, llmConfig?.defaultTier, llmConfig?.verifyTier]);

  useEffect(() => {
    if (verifyIterationsData?.maxIterations !== undefined) {
      setVerifyIterationsSelection(String(verifyIterationsData.maxIterations));
    }
  }, [verifyIterationsData?.maxIterations]);

  const llmConfigDirty =
    !!llmConfig &&
    (providerSelection !== llmConfig.provider ||
      defaultTierSelection !== llmConfig.defaultTier ||
      verifyTierSelection !== llmConfig.verifyTier);

  // Backend-resolved model ID for the currently selected provider/tier, so the
  // label reflects env overrides instead of hardcoded constants.
  const resolvedModelId = (tier: "thinking" | "small"): string | undefined =>
    llmConfig?.models?.[providerSelection]?.[tier];

  const handleSaveLlmConfig = async () => {
    setSavingLlmConfig(true);
    try {
      await apiRequest("PUT", "/api/admin/llm-config", {
        provider: providerSelection,
        defaultTier: defaultTierSelection,
        verifyTier: verifyTierSelection,
      });
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/llm-config"] });
      // /api/models/config feeds every tier picker (Dashboard/ChatDashboard/SideChat),
      // so invalidate it too for the provider/tier change to reflect instantly.
      await queryClient.invalidateQueries({ queryKey: ["/api/models/config"] });
      toast({ title: "Provider settings saved", description: "All future generations will use the new settings." });
    } catch (err) {
      toast({
        title: "Couldn't save provider settings",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setSavingLlmConfig(false);
    }
  };

  const handleSaveVerifyIterations = async () => {
    if (!verifyIterationsSelection) return;
    setSavingVerifyIterations(true);
    try {
      await apiRequest("PUT", "/api/admin/verify-iterations", {
        maxIterations: Number(verifyIterationsSelection),
      });
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/verify-iterations"] });
      toast({ title: "Verification retries updated", description: "New documents will use this setting." });
    } catch (err) {
      toast({
        title: "Couldn't update verification retries",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setSavingVerifyIterations(false);
    }
  };

  const handleResetAll = async () => {
    setResettingAll(true);
    try {
      await apiRequest("POST", "/api/admin/models/reset-all", {});
      setResetDialogOpen(false);
      toast({ title: "Reset applied", description: "All users will use the default tier on their next generation." });
    } catch (err) {
      toast({
        title: "Couldn't reset users",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setResettingAll(false);
    }
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-2xl mx-auto px-6 py-8 space-y-8">
        <div>
          <h1 className="text-xl font-bold text-foreground">Models</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Choose the AI provider and model tiers used for document generation and verification.
          </p>
        </div>

        {/* Provider & tiers */}
        <section>
          <h2 className="text-base font-semibold text-foreground mb-3">Provider &amp; model tiers</h2>
          <div className="rounded-lg border border-border bg-card px-4 py-4 space-y-5" data-testid="section-llm-config">
            {llmConfigLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading…
              </div>
            ) : (
              <>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">Provider</label>
                  <p className="text-xs text-muted-foreground">Which AI service to route all LLM calls through.</p>
                  <Select value={providerSelection} onValueChange={(v) => setProviderSelection(v as "requesty" | "bedrock")}>
                    <SelectTrigger className="w-[240px]" data-testid="select-llm-provider">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="requesty" data-testid="option-provider-requesty">Requesty AI</SelectItem>
                      <SelectItem value="bedrock" data-testid="option-provider-bedrock">AWS Bedrock</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">Generation tier</label>
                  <p className="text-xs text-muted-foreground">
                    Model tier used for document generation and all other AI text features.
                    {resolvedModelId(defaultTierSelection) && (
                      <span className="ml-1 font-mono text-foreground/70" data-testid="text-default-tier-model">
                        → {resolvedModelId(defaultTierSelection)}
                      </span>
                    )}
                  </p>
                  <Select value={defaultTierSelection} onValueChange={(v) => setDefaultTierSelection(v as "thinking" | "small")}>
                    <SelectTrigger className="w-[280px]" data-testid="select-default-tier">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="thinking" data-testid="option-default-tier-thinking">{TIER_LABELS.thinking}</SelectItem>
                      <SelectItem value="small" data-testid="option-default-tier-small">{TIER_LABELS.small}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">Verification tier</label>
                  <p className="text-xs text-muted-foreground">
                    Model tier used for compliance-checking generated sections.
                    {resolvedModelId(verifyTierSelection) && (
                      <span className="ml-1 font-mono text-foreground/70" data-testid="text-verify-tier-model">
                        → {resolvedModelId(verifyTierSelection)}
                      </span>
                    )}
                  </p>
                  <Select value={verifyTierSelection} onValueChange={(v) => setVerifyTierSelection(v as "thinking" | "small")}>
                    <SelectTrigger className="w-[280px]" data-testid="select-verify-tier">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="thinking" data-testid="option-verify-tier-thinking">{TIER_LABELS.thinking}</SelectItem>
                      <SelectItem value="small" data-testid="option-verify-tier-small">{TIER_LABELS.small}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  size="sm"
                  onClick={handleSaveLlmConfig}
                  disabled={savingLlmConfig || !llmConfigDirty}
                  data-testid="button-save-llm-config"
                >
                  {savingLlmConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save provider settings"}
                </Button>
              </>
            )}
          </div>
        </section>

        {/* Reset all users */}
        <section>
          <h2 className="text-base font-semibold text-foreground mb-3">User overrides</h2>
          <div
            className="rounded-lg border border-primary/40 bg-primary/5 px-4 py-4 flex items-start gap-3"
            data-testid="section-default-model"
          >
            <div className="mt-0.5 p-1.5 rounded-md bg-primary/10 shrink-0">
              <Star className="w-4 h-4 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
                <p className="text-sm text-muted-foreground">
                  Overrides every user's personal tier choice — they'll use the default generation tier on their next generation.
                </p>
                <div className="mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setResetDialogOpen(true)}
                    data-testid="button-reset-everyone-to-default"
                  >
                    Reset everyone to default tier
                  </Button>
                </div>
              </div>
            </div>
          </section>

        {/* Verification retries */}
        <section>
          <h2 className="text-base font-semibold text-foreground mb-3">Verification retries</h2>
          <div
            className="rounded-lg border border-border bg-card px-4 py-4 flex items-start gap-3"
            data-testid="section-verify-iterations"
          >
            <div className="mt-0.5 p-1.5 rounded-md bg-muted shrink-0">
              <ShieldCheck className="w-4 h-4 text-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-muted-foreground">
                Maximum verify-and-regenerate passes per section. 0 disables verification entirely; 1 means each section is checked once with no regeneration.
              </p>
              {verifyIterationsLoading ? (
                <div className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading…
                </div>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground mt-2">
                    Current:{" "}
                    <span className="font-mono text-foreground" data-testid="text-verify-iterations-current">
                      {verifyIterationsData?.maxIterations}
                    </span>
                    {verifyIterationsData?.source === "default" && (
                      <span className="ml-2 text-muted-foreground/70">· default</span>
                    )}
                  </p>
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    <Select
                      value={verifyIterationsSelection}
                      onValueChange={setVerifyIterationsSelection}
                    >
                      <SelectTrigger className="w-[120px]" data-testid="select-verify-iterations">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from(
                          { length: (verifyIterationsData?.limit ?? 5) + 1 },
                          (_, i) => i,
                        ).map((n) => (
                          <SelectItem
                            key={n}
                            value={String(n)}
                            data-testid={`option-verify-iterations-${n}`}
                          >
                            {n}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      onClick={handleSaveVerifyIterations}
                      disabled={
                        savingVerifyIterations ||
                        !verifyIterationsSelection ||
                        verifyIterationsSelection === String(verifyIterationsData?.maxIterations)
                      }
                      data-testid="button-save-verify-iterations"
                    >
                      {savingVerifyIterations ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

      </div>

      <AlertDialog open={resetDialogOpen} onOpenChange={(open) => { if (!open && !resettingAll) setResetDialogOpen(false); }}>
        <AlertDialogContent data-testid="dialog-reset-everyone">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset everyone to default tier?</AlertDialogTitle>
            <AlertDialogDescription>
              This will override every user's personal tier choice. All users will use the default generation tier on their next document generation.
              Existing documents are not changed immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resettingAll} data-testid="button-cancel-reset-everyone">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetAll}
              disabled={resettingAll}
              data-testid="button-confirm-reset-everyone"
            >
              {resettingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Reset everyone
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface IdentitySyncResult {
  total: number;
  succeeded: number;
  failed: number;
  failures: { userId: string; email: string | null; error: string }[];
}

function AdminIdentitiesPage() {
  const { toast } = useToast();
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState<IdentitySyncResult | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);

  const { data: statusData, isLoading: statusLoading } = useQuery<{
    userCount: number;
    configured: boolean;
  }>({
    queryKey: ["/api/admin/identities/status"],
  });

  const handleSync = async () => {
    setSyncing(true);
    setResult(null);
    setNotConfigured(false);
    try {
      const resp = await apiRequest("POST", "/api/admin/identities/sync", {});
      const data = (await resp.json()) as IdentitySyncResult;
      setResult(data);
      if (data.failed === 0) {
        toast({ title: "Sync complete", description: `All ${data.succeeded} users registered successfully.` });
      } else {
        toast({
          title: "Sync finished with errors",
          description: `${data.succeeded} succeeded, ${data.failed} failed.`,
          variant: "destructive",
        });
      }
    } catch (err: any) {
      const msg: string = err?.message || "";
      if (msg.includes("IDENTITY_STORE_TOKEN") || err?.status === 503) {
        setNotConfigured(true);
      } else {
        toast({
          title: "Sync failed",
          description: msg || "An unexpected error occurred.",
          variant: "destructive",
        });
      }
    } finally {
      setSyncing(false);
    }
  };

  const isNotConfigured = notConfigured || (statusData && !statusData.configured);

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-2xl mx-auto px-6 py-8 space-y-8">
        <div>
          <h1 className="text-xl font-bold text-foreground">Identities</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Register all users into the external identity store. The store maps each person to their
            multiple issuers and is used to support multi-identity logins. Re-registering is safe — the
            identity store endpoint is idempotent.
          </p>
        </div>

        {isNotConfigured && (
          <div
            className="flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-4"
            data-testid="notice-identity-not-configured"
          >
            <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-destructive">IDENTITY_STORE_TOKEN not configured</p>
              <p className="text-xs text-muted-foreground mt-1">
                Set the <span className="font-mono">IDENTITY_STORE_TOKEN</span> secret in your Replit environment to enable identity sync.
              </p>
            </div>
          </div>
        )}

        <section className="space-y-4">
          <div className="rounded-lg border border-border bg-card px-4 py-4 flex items-start gap-3">
            <div className="mt-0.5 p-1.5 rounded-md bg-muted shrink-0">
              <Fingerprint className="w-4 h-4 text-foreground" />
            </div>
            <div className="min-w-0 flex-1 space-y-3">
              <div>
                <p className="text-sm font-medium text-foreground">Register identities</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Sends every user in the Postgres <span className="font-mono text-xs">users</span> table
                  to the identity store at{" "}
                  <span className="font-mono text-xs">https://identity-store-production.up.railway.app</span>.
                </p>
              </div>
              {statusLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading…
                </div>
              ) : (
                <p className="text-xs text-muted-foreground" data-testid="text-identity-user-count">
                  <span className="font-semibold text-foreground">{statusData?.userCount ?? "—"}</span> users in database
                </p>
              )}
              <Button
                size="sm"
                onClick={handleSync}
                disabled={syncing || !!isNotConfigured}
                data-testid="button-register-identities"
              >
                {syncing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Registering…
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Register identities
                  </>
                )}
              </Button>
            </div>
          </div>
        </section>

        {result && (
          <section className="space-y-3" data-testid="section-identity-results">
            <h2 className="text-base font-semibold text-foreground">Last sync result</h2>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border border-border bg-card p-3 text-center" data-testid="stat-identity-total">
                <p className="text-2xl font-bold text-foreground">{result.total}</p>
                <p className="text-xs text-muted-foreground mt-1">Total</p>
              </div>
              <div className="rounded-lg border border-green-500/30 bg-green-500/5 p-3 text-center" data-testid="stat-identity-succeeded">
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{result.succeeded}</p>
                <p className="text-xs text-muted-foreground mt-1">Succeeded</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${result.failed > 0 ? "border-destructive/30 bg-destructive/5" : "border-border bg-card"}`} data-testid="stat-identity-failed">
                <p className={`text-2xl font-bold ${result.failed > 0 ? "text-destructive" : "text-foreground"}`}>{result.failed}</p>
                <p className="text-xs text-muted-foreground mt-1">Failed</p>
              </div>
            </div>

            {result.failed === 0 ? (
              <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400" data-testid="notice-identity-all-succeeded">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                All users registered successfully.
              </div>
            ) : (
              <div className="space-y-2" data-testid="list-identity-failures">
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <XCircle className="w-4 h-4 shrink-0" />
                  {result.failed} user{result.failed !== 1 ? "s" : ""} could not be registered.
                </div>
                <div className="rounded-lg border border-border bg-card overflow-hidden">
                  <ul className="divide-y divide-border text-xs">
                    {result.failures.map((f) => (
                      <li
                        key={f.userId}
                        className="px-4 py-2.5 space-y-0.5"
                        data-testid={`row-identity-failure-${f.userId}`}
                      >
                        <p className="font-medium text-foreground">{f.email ?? f.userId}</p>
                        <p className="text-muted-foreground font-mono">{f.error}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

type AdminSection = "settings" | "site-admins" | "usage" | "models" | "identities";

export default function AdminArea({ section }: { section: AdminSection | null }) {
  const { user, logout, isLoading: authLoading } = useAuth();
  const { data: adminCheckData, isLoading: adminLoading } = useQuery<{ isAdmin: boolean }>({
    queryKey: ["/api/admin/is-admin"],
    enabled: !!user,
  });
  const [, navigate] = useLocation();

  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/api/login";
    }
    if (!adminLoading && adminCheckData?.isAdmin === false) {
      navigate("/dashboard");
    }
  }, [authLoading, user, adminLoading, adminCheckData, navigate]);

  if (authLoading || adminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || !adminCheckData?.isAdmin) return null;

  return (
    <div className="h-screen flex flex-col bg-background">
      <SEO title={`Admin — LRScribe`} noindex />
      <AdminHeader user={user} logout={logout} />
      <div className="flex flex-1 overflow-hidden">
        <AdminSidebar />
        {!section && <AdminMobileMenu />}
        <div
          className={cn(
            "flex-1 flex-col overflow-hidden",
            section ? "flex" : "hidden md:flex",
          )}
        >
          {section && <AdminMobileBackBar />}
          {(section ?? "settings") === "settings" && <AdminSettingsPage />}
          {section === "site-admins" && <AdminSiteAdminsPage />}
          {section === "usage" && <AdminUsagePage />}
          {section === "models" && <AdminModelsPage />}
          {section === "identities" && <AdminIdentitiesPage />}
        </div>
      </div>
    </div>
  );
}
